import Value, { getValue, setValue } from "aldoo-ra/Value"
import { useEffect, useRef, useState } from "react"
import AldooStoreAPI from "aldoo-ra/Store/api"
import { priceDisplayFormat } from "aldoo-ra/CMSModels/StorePrice/display-formatters"
import StoreRender from "aldoo-ra/Store/store-render"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import PubSub from "pubsub-js"
import Modal from "aldoo-ra/Modal"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { api } from "aldoo-ra/API"
import { loadFlows, createFlowInstance } from "aldoo-ra/Store/StorePurchaseFlow"
import { prefetchStripeConfig } from "aldoo-ra/Store/stripe-client"
import { lineKey as defaultLineKey, withSelection } from "aldoo-ra/Store/lines"

/**
 * The mounted store, kept on the global rather than in module scope.
 *
 * `store` below is a proxy over it, and every caller outside React imports
 * that proxy. Held in a module variable, a hot reload of this file makes a
 * second copy of the module whose variable is null while the mounted tree
 * still belongs to the first — and `store.addToCart` becomes undefined until
 * the page is reloaded by hand. A global slot is the same object for both.
 */
const INSTANCE = Symbol.for("aldoo.store.instance")
const mountedStore = () => globalThis[INSTANCE] || null
let flowsLoaded = false

/**
 * Store configuration. Every field has the historical default, so a store
 * mounted as <AldooStore /> behaves exactly as before; a project passes only
 * what it changes: <AldooStore flow="shop" config={{ multiItem: true }} />.
 */
export const DEFAULT_STORE_CONFIG = {
  // false: one product per cart (adding replaces the cart, quantity via product.add/remove)
  // true: several lines, addToCart merges by line key (aldoo-ra/Store/lines) and honours quantity/options
  multiItem: false,
  // Line identity for multi-item carts: product_id || pid for a plain line,
  // pid + sku + choices + components for a line with a buyer's selection
  lineKey: defaultLineKey,
  // Existing/new accounts must click the magic link before the checkout continues.
  // false: a brand new email creates the account and proceeds right away
  // (the API answers `create` with an auth_token); existing accounts still verify.
  verifyNewUsers: true,
  // Sign-in buttons on the checkout's email step, beside typing the address ("google"): one tap gives a
  // signed-in buyer and their email. [] shows only the email field.
  emailStepProviders: ["google"],
  // Load the saved billing details (store {get_billing_details}) when the checkout opens
  loadBillingDetails: true,
  // Drop persisted cart lines whose pid is missing from store {get_pricing_data}
  validateCartPids: true,
  payment: {
    // Sync the buyer email to the payment customer before confirming (store {update-customer-email})
    syncCustomerEmail: true,
    // Discard a persisted paymentInfo when the checkout opens (fresh intent per checkout)
    reinitializeOnOpen: false,
  },
  // Name of the query parameter that says the cart is open ("cart" → ?cart=1),
  // so a reload or a shared link comes back to the open cart. null: off, and
  // the URL never changes.
  cartUrlParam: null,
  // The same for an open checkout ("checkout" → ?checkout=1): a reload comes
  // back into the checkout (its first step) as long as the cart has lines.
  checkoutUrlParam: null,
  // Modal look of the store dialogs: { variant, className, styleOverrides }
  modals: {
    cart: {},
    // checkout also takes allowClose (close button + Escape) and confirmClose ({ title, message, confirmText, cancelText } | true)
    // and, for the phone step strip's pointer arrow, stepArrow: false (none) or
    // stepArrowSrc / stepArrowClassName (a static image where the CMS has no "arrow_menu_mobile" icon)
    checkout: {},
    success: {},
    error: {},
  },
}

/**
 * The open cart in the address bar. The parameter is written with
 * replaceState, so opening the cart adds no history entry — the back button
 * still leaves the page, as it did before the cart knew about the URL.
 */
const cartUrlWrite = (param, open) => {
  if (!param || typeof window === "undefined") return
  const url = new URL(window.location.href)
  const current = url.searchParams.get(param)
  if (current === (open ? "1" : null)) return
  // a closed cart takes away only its own "1": the same name with another value is
  // another screen's (the admin's carts page opens a cart as ?cart=<id>)
  if (!open && current !== "1") return
  if (open) url.searchParams.set(param, "1")
  else url.searchParams.delete(param)
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`)
}

const cartUrlHas = (param) => {
  if (!param || typeof window === "undefined") return false
  return new URLSearchParams(window.location.search).get(param) === "1"
}

const mergeConfig = (config = {}) => ({
  ...DEFAULT_STORE_CONFIG,
  ...config,
  payment: { ...DEFAULT_STORE_CONFIG.payment, ...(config.payment || {}) },
  modals: {
    cart: { ...(config.modals?.cart || {}) },
    checkout: { ...(config.modals?.checkout || {}) },
    success: { ...(config.modals?.success || {}) },
    error: { ...(config.modals?.error || {}) },
  },
})

// Response of user {internal_id}: { data } (framework API), { result } (Books API) or a bare id
const unwrapInternalId = (response) => response?.data || response?.result || response

// ── GA4 helpers ───────────────────────────────────────────────────────────────
// Extract the effective unit price from a cart item (handles price_model phases)
const getItemPrice = (item) => {
  const priceModel = item.price_model?.find(pm => pm.primary)
  const phases = priceModel?.phases
  const introPhase = phases?.find(p => p.phase_type === 'introductory' && p.enabled !== false)
  const regularPhase = phases?.find(p => p.phase_type === 'regular')
  if (priceModel?.type === 'real') {
    return introPhase?.amount ?? regularPhase?.amount ?? priceModel?.amount ?? 0
  }
  return item.amount ?? item.price ?? 0
}

// Build a GA4-spec item object from a cart item
const buildGA4Item = (item) => ({
  item_id:   item.pid || item.product_id || item.id,
  item_name: item.name || item.pid || item.product_id,
  price:     getItemPrice(item),
  quantity:  item.quantity || 1,
})

/**
 * How the cart's lines read as GA4 items, and in which currency: the plain
 * reading above, which a project replaces with its own (the seller, the
 * categories, the option chosen, prices in the cart's currency) through
 * setStoreTracking({ item(line), currency(lines) }).
 */
const storeTracking = {
  item: (line) => buildGA4Item(line),
  currency: (lines) => lines?.[0]?.currency,
}
export const setStoreTracking = (adapter = {}) => Object.assign(storeTracking, adapter)

/** A cart event's payload: the lines as items, what they come to, the currency. */
const cartEvent = (lines, extra = {}) => {
  const items = (lines || []).map((line) => storeTracking.item(line)).filter(Boolean)
  const value = items.reduce((sum, item) => sum + (Number(item.price) || 0) * (item.quantity || 1), 0)
  const currency = storeTracking.currency(lines || [])
  return { currency: currency ? String(currency).toUpperCase() : undefined, value, items, ...extra }
}

/**
 * This component is used to handle the purchase product lifecycle.
 * It aim to maintain compatibility with the purchase flow introduced in google analytics
 * Session Start - the user
 * View Product -
 * Add To Cart -
 * Begin Checkout -
 * Purchase
 */
export default function AldooStore({ flow = 'default', config: configProp }) {
  const [purchaseFlow, setPurchaseFlow] = useState(null)
  const config = mergeConfig(configProp)
  // Set right before hideCheckout() on a successful payment so the
  // STORE_CHECKOUT_CLOSED event can tell an abandoned checkout from a completed one
  const checkoutCompletedRef = useRef(false)

  // Load flows on mount
  useEffect(() => {
    const initializeFlows = async () => {
      if (!flowsLoaded) {
        await loadFlows()
        flowsLoaded = true
      }
      // Create flow instance
      const flowInstance = createFlowInstance(flow)
      setPurchaseFlow(flowInstance)
    }

    initializeFlows()
    // Prefetch only the publishable key — Stripe.js itself downloads when
    // a payment UI first renders (getStripePromise), not on every page
    prefetchStripeConfig()
  }, [flow])

  //This is the Aldoo Store cart that holds the products
  const [cart, setCart] = Value({
    key: "aldoo-store-cart",
    defaultValue: [],
    persistKey: "aldoo-store-cart",
  })
  //This is the Aldoo Store payment info that holds the payment details
  //Like a receipt in the shopping cart so the success screen can model its message
  const [paymentInfo, setPaymentInfo] = Value({
    key: "aldoo-payment-info",
    defaultValue: null,
    persistKey: "aldoo-payment-info",
  })


  //Preloaded invoice data (for users with existing billing details)
  const [preloadedInvoiceData, setPreloadedInvoiceData] = Value({
    key: "aldoo-preloaded-invoice-data",
    defaultValue: null,
  })

  //Get the auth state
  const [auth, setAuth] = PlatformAuth.useAuth()

  const [promoCodeInfo, setPromoCodeInfo] = Value({
    key: "aldoo-promo-code-info",
    defaultValue: null,
    persistKey: "aldoo-promo-code-info",
  })

  const [promoCode, setPromoCode] = Value({
    key: "aldoo-promo-code",
    defaultValue: "",
    persistKey: "aldoo-promo-code",
  })

  //Once a payment is successful, we store the payment info here
  //Since the original payment info is cleared
  //The receipt holds: { paymentInfo, cart }
  const [paymentReceipt, setPaymentReceipt] = Value({
    key: "aldoo-payment-receipt",
    defaultValue: null,
    persistKey: "aldoo-payment-receipt",
  })

  // Extra charges added to the cart total by checkout steps
  // (e.g. shipping picked in a delivery step): [{ id, label, amount, currency }]
  const [cartExtras, setCartExtras] = Value({
    key: "aldoo-store-cart-extras",
    defaultValue: [],
  })

  const [cartVisible, setCartVisible] = useState(false)
  const [emailDialogVisible, setEmailDialogVisible] = useState(false)
  const [checkoutVisible, setCheckoutVisible] = useState(false)
  const [paymentSuccessVisible, setPaymentSuccessVisible] = useState(false)
  const [paymentError, setPaymentError] = useState(null)
  const [promoCodeError, setPromoCodeError] = useState(null)

  // Track if this purchase will need email verification after completion
  const [purchaseRequiresVerification, setPurchaseRequiresVerification] = useState(false)
  const [purchaseUserEmail, setPurchaseUserEmail] = useState(null)
  const [purchaseUserCredentials, setPurchaseUserCredentials] = useState(null)

  // Store temporary auth for existing users (doesn't log them into the app)
  const [tempAuthForPurchase, setTempAuthForPurchase] = useState(null)

  // Use Value for in-memory storage without persistence (no key = no localStorage)
  const [paymentIntentId, setPaymentIntentId] = Value({ defaultValue: null })
  const [subscriptionId, setSubscriptionId] = Value({ defaultValue: null })

  //When the cart changes, the checkout should reinitialize a payment
  //Otherwise, the cart is just for viewing
  const [cartChanged, setCartChanged] = useState(false)

  useEffect(() => {
    //there is no paymentInfo or the cart hasn't changed
    if (!cartChanged) return
    // Clean up old subscription
    cleanupOldSubscription()
    // Clear existing payment info as it's no longer valid
    setPaymentInfo(null)
  }, [cartChanged])

  // Proactive cart-pid guard. The cart is persisted in localStorage, so it can
  // survive the API being repointed at a different DB/env — leaving pids that
  // no longer exist. Once, on mount, validate persisted pids against the live
  // catalog and drop any that are gone (clearing the now-invalid paymentInfo).
  useEffect(() => {
    if (!cart.length || !config.validateCartPids) return
    let active = true
    AldooStoreAPI.getPricingData()
      .then((data) => {
        if (!active) return
        const valid = new Set(
          Object.values(data?.products || {}).map((p) => p?.pid).filter(Boolean)
        )
        // Empty/failed catalog fetch — don't wipe an otherwise valid cart.
        if (!valid.size) return
        const pruned = cart.filter((i) => valid.has(i.pid))
        if (pruned.length !== cart.length) {
          setCart(pruned)
          setPaymentInfo(null)
        }
      })
      .catch(() => {})
    return () => { active = false }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for add-to-cart-and-show event from PromoBanner
  useEffect(() => {
    const addToken = PubSub.subscribe('add-to-cart-and-show', (msg, data) => {
      if (data.product) {
        addToCart({
          product: data.product,
          proceedToCart: true
        })
      }
    })
    const removeToken = PubSub.subscribe('remove-from-cart', (msg, data) => {
      if (!data?.pid) return
      const current = getValue('aldoo-store-cart', 'aldoo-store-cart') || []
      const removedItem = current.find(item => item.pid === data.pid)
      const updatedCart = current.filter(item => item.pid !== data.pid)
      setCart(updatedCart)
      setCartChanged(true)
      if (removedItem) PubSub.publish('Tracking_RemoveFromCart', cartEvent([removedItem]))
    })
    const discountToken = PubSub.subscribe('STORE_APPLY_DISCOUNT', async (msg, data) => {
      if (!data?.code) return
      const code = data.code.toUpperCase()
      // Use module-level setValue directly — avoids stale-closure issues with the Value hook wrapper
      setValue('aldoo-promo-code', code, 'aldoo-promo-code')
      setPromoCodeError(null)
      setCartChanged(true)
      setCartVisible(true)
      // Validate and apply the code immediately so the discounted price shows on open
      const response = await AldooStoreAPI.getPromoCodeInfo(code)
      if (response.error) {
        const err = response.error === 'coupon_already_used' && response.reason
          ? response.reason
          : response.error
        setPromoCodeError(err)
        return
      }
      if (response.result === true) {
        setPromoCodeError(null)
        setValue('aldoo-promo-code-info', response, 'aldoo-promo-code-info')
      }
    })
    return () => {
      PubSub.unsubscribe(addToken)
      PubSub.unsubscribe(removeToken)
      PubSub.unsubscribe(discountToken)
    }
  }, [])

  //on free purchases announce the payment success immediately
  useEffect(() => {
    if (paymentInfo && !paymentInfo.requiresPayment) onPaymentSuccess()
  }, [paymentInfo])

  //Upon new receipt , show the success screen
  useEffect(() => {
    if (!paymentReceipt || !paymentInfo) return

    // Capture promo state before emptyCart clears it
    const appliedCoupon = getValue('aldoo-promo-code') || undefined
    const savedPromoInfo = getValue('aldoo-promo-code-info')

    //empty the cart & reset paymentInfo
    emptyCart()
    //show the payment success screen
    setPaymentSuccessVisible(true)
    // Announce payment success globally
    PubSub.publish("payment-success", paymentReceipt)

    // Track purchase with actual paid amount (post-discount)
    const receiptCart = paymentReceipt.cart || []
    if (receiptCart.length) {
      // Landing page / campaign origin persisted by purchase deep links
      // (e.g. /buy?utm_source=lp-android-1) — stamps the purchase in GA4
      const purchaseOrigin =
        typeof sessionStorage !== 'undefined'
          ? sessionStorage.getItem('aldoo-purchase-origin')
          : null
      const event = cartEvent(receiptCart)
      const items = event.items.map((i) => (purchaseOrigin ? { ...i, item_list_name: purchaseOrigin } : i))
      // A payment initializer that knows the charged amount (incl. extras such
      // as shipping) reports it in paymentInfo.total (major units)
      const paid = paymentReceipt.paymentInfo || {}
      const value = typeof paid.total === 'number' ? paid.total : calculateDiscountedTotal(event.value, savedPromoInfo)
      const currency = paid.currency || event.currency
      PubSub.publish('Tracking_Purchase', {
        currency: currency ? String(currency).toUpperCase() : undefined,
        value,
        price: value,
        items,
        // the payment's id: the same one the server knows the order by (a
        // report of its own, a refund, joins this purchase through it)
        transactionId: paymentReceipt.paymentIntentId || paid.paymentIntentId || paymentReceipt.subscriptionId,
        // how it was paid: an order placed to be paid on delivery, or a card (purchases split by it in GA4)
        payment_type: paymentReceipt.cashOnDelivery ? 'cash_on_delivery' : paymentReceipt.subscriptionId ? undefined : 'card',
        ...(typeof paid.shipping === 'number' ? { shipping: paid.shipping } : {}),
        ...(typeof paid.tax === 'number' ? { tax: paid.tax } : {}),
        // Set only for subscriptions; lets trackers report them as a separate goal
        subscriptionId: paymentReceipt.subscriptionId || undefined,
        coupon: appliedCoupon,
        origin: purchaseOrigin,
        // The buyer for enhanced conversions (Google Ads hashes them)
        email: paymentReceipt.email || undefined,
        phone: paymentReceipt.phone || paid.phone || undefined,
      })
    }
  }, [paymentReceipt, paymentInfo])


  const cleanupOldSubscription = async () => {
    const { subscriptionId } = paymentInfo || {}

    if (!subscriptionId) return

    try {
      // Call the API endpoint that will invoke the server-side method
      const response = await AldooStoreAPI.cancelIncompleteSubscription(
        subscriptionId
      )
      // if (response.result) {
      //   console.log("Old subscription cancelled successfully")
      // }

      // if (response.error) {
      //   console.error("Error cancelling old subscription:", response.error)
      // }
    } catch (error) {
      // console.error("Error cleaning up old subscription:", error)
    }
  }

  /**
   * Get an element for rendering from the project or the default elements
   * @param {*} name
   */
  const getElement = (name) => {
    return StoreRender.getElement(name)
  }

  const getFragment = (name) => {
    return StoreRender.getFragment(name)
  }

  const refreshCart = () => {
    setCart([...mountedStore().cart])
  }

  const emptyCart = () => {
    setCart([])
    setCartExtras([])
    setPaymentInfo(null)
    setPaymentError(null)
    resetPromoCode()
    //mark as changed
    setCartChanged(true)
    PubSub.publish('STORE_CART_CHANGED', { cart: [] })
  }

  // The cart the URL asks for: opened once the store is mounted (the cart
  // itself comes from localStorage, so this runs on the client only).
  useEffect(() => {
    if (!config.cartUrlParam) return
    if (!cartUrlHas(config.cartUrlParam)) return
    setCartVisible(true)
    PubSub.publish('STORE_CART_OPENED')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // …and the URL follows the cart from then on, however it was opened or closed
  useEffect(() => {
    cartUrlWrite(config.cartUrlParam, cartVisible)
  }, [cartVisible, config.cartUrlParam])

  // The checkout the URL asks for: begun once the cart is in (it is read from
  // localStorage after mount), the way the cart's own button begins it
  useEffect(() => {
    if (!config.checkoutUrlParam) return
    if (!cartUrlHas(config.checkoutUrlParam)) return
    if (!cart.length) return
    beginCheckout()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.length > 0])

  useEffect(() => {
    cartUrlWrite(config.checkoutUrlParam, checkoutVisible)
  }, [checkoutVisible, config.checkoutUrlParam])

  // the payment being made (elements/use-payment-submission): GA4 add_payment_info, once per checkout
  const paymentReported = useRef(null)
  useEffect(() => {
    const token = PubSub.subscribe('STORE_PAYMENT_SUBMIT', (_, data) => {
      const lines = currentCart()
      const key = (paymentInfo?.paymentIntentId || '') + lines.map((line) => config.lineKey(line)).join(',')
      if (!lines.length || paymentReported.current === key) return
      paymentReported.current = key
      PubSub.publish('Tracking_AddPaymentInfo', cartEvent(lines, { payment_type: data?.payment_type || 'card', ...(paymentInfo?.total ? { value: paymentInfo.total } : {}) }))
    })
    return () => PubSub.unsubscribe(token)
  })

  const showCart = () => {
    //reset the cart changed flag
    setCartChanged(false)
    //clear promo code error for new checkout session
    setPromoCodeError(null)
    setCartVisible(true)
    PubSub.publish('STORE_CART_OPENED')
    const lines = currentCart()
    if (lines.length) PubSub.publish('Tracking_ViewCart', cartEvent(lines))
  }

  const hideCart = () => {
    setCartVisible(false)
    PubSub.publish('STORE_CART_CLOSED')
  }

  const showCheckout = () => {
    setCheckoutVisible(true)
  }

  /**
   * Multi-item cart helpers (config.multiItem). Lines are matched by
   * config.lineKey; `options` is a free-form object a project uses for the
   * line's extra data (platform distribution, seller, variant…).
   */
  const findLine = (key) => (getValue("aldoo-store-cart", "aldoo-store-cart") || cart).find((item) => config.lineKey(item) === key)

  const updateLines = (updated) => {
    setCart(updated)
    setCartChanged(true)
    PubSub.publish('STORE_CART_CHANGED', { cart: updated })
  }

  /**
   * The cart as it is now, not as it was when this render began.
   *
   * The lines live in the Value store, which is written synchronously, while
   * `cart` is the copy this render closed over. Two changes made in one
   * handler — the licence dialog sets a line's options and then its quantity —
   * both mapped over that same old copy, so the second threw the first away
   * and the dialog looked like it had done nothing.
   */
  const currentCart = () => getValue("aldoo-store-cart", "aldoo-store-cart") || cart

  // a line's quantity raised or lowered: an add or a removal of the difference (GA4)
  const trackQuantityChange = (before, quantity) => {
    const delta = before && quantity !== undefined ? quantity - (before.quantity || 1) : 0
    if (delta > 0) PubSub.publish('Tracking_AddToCart', cartEvent([{ ...before, quantity: delta }]))
    if (delta < 0) PubSub.publish('Tracking_RemoveFromCart', cartEvent([{ ...before, quantity: -delta }]))
  }

  const setQuantity = (key, quantity) => {
    if (quantity <= 0) return removeItem(key)
    trackQuantityChange(findLine(key), quantity)
    updateLines(currentCart().map((item) => (config.lineKey(item) === key ? { ...item, quantity } : item)))
  }

  const setItemOptions = (key, options) => {
    updateLines(currentCart().map((item) => (config.lineKey(item) === key ? { ...item, options: { ...(item.options || {}), ...options } } : item)))
  }

  /**
   * Quantity, options and selection of one line, in a single change. A
   * selection ({ sku, choices, choiceLabels, components }) changes what the
   * line is, so the line takes its new key with it.
   */
  const setLine = (key, { quantity, options, selection }) => {
    if (quantity !== undefined && quantity <= 0) return removeItem(key)
    trackQuantityChange(findLine(key), quantity)
    updateLines(
      currentCart().map((item) => {
        if (config.lineKey(item) !== key) return item
        const next = {
          ...item,
          ...(quantity !== undefined ? { quantity } : {}),
          ...(options ? { options: { ...(item.options || {}), ...options } } : {}),
        }
        return selection ? withSelection(next, selection) : next
      })
    )
  }

  const removeItem = (key) => {
    const removedItem = findLine(key)
    if (!removedItem) return
    updateLines(currentCart().filter((item) => config.lineKey(item) !== key))
    PubSub.publish('Tracking_RemoveFromCart', cartEvent([removedItem]))
  }

  const resetPromoCode = () => {
    setPromoCode("")
    setPromoCodeError(null)
    setPromoCodeInfo(null)
    //mark as changed
    setCartChanged(true)
  }

  const hideCheckout = () => {
    // Stop any magic-link wait still polling IsDeviceLogged once a second —
    // awaitAuth loops until it is broken, so closing the modal would otherwise
    // leave it running for the life of the tab
    PlatformAuth.SetBreakAwaitAuth(true)
    setCheckoutVisible(false)
    // Lets a project react to an abandoned checkout (release a stock
    // reservation, log the drop-off…)
    PubSub.publish('STORE_CHECKOUT_CLOSED', {
      completed: checkoutCompletedRef.current,
      paymentInfo,
      cart,
    })
    checkoutCompletedRef.current = false
  }

  /**
   * Handle email verification and user setup before checkout
   */
  const handleEmailVerification = async (email) => {
    const { RequestInternalID, CreateUser, VerifyCredentials, Login } = await import("aldoo-ra/PlatformAuth/api")
    const ensureDevice = (await import("aldoo-ra/PlatformAuth/actions/ensure-device")).default
    const awaitAuth = (await import("aldoo-ra/PlatformAuth/actions/await-auth")).default
    const { api } = await import("aldoo-ra/API")

    // Use ensureDevice to get the device (same logic as signin flow)
    // This ensures we use the same fingerprint-matched device
    const deviceID = await ensureDevice({ email })
    const credentials = { email, deviceID }

    // Check if email belongs to existing user (verify returns the account id only to a device signed in to it)
    const { result: emailExists } = await VerifyCredentials(credentials)

    if (emailExists) {
      // Email belongs to existing user
      setPurchaseRequiresVerification(true)
      setPurchaseUserEmail(email)
      setPurchaseUserCredentials(credentials)

      // Always call safe_logout first to ensure clean state (unless already logged in with same credentials)
      if (!auth || auth.email !== email || auth.deviceID !== deviceID) {
        const userAPI = api("user")
        await userAPI({
          safe_logout: { email, deviceID }
        })
      }

      // Trigger Login API - this will send magic link if device not logged in
      const loginResponse = await Login(credentials, { fetch_user_fields: ["_id"] })

      // If device already logged in, proceed immediately
      if (loginResponse.result) {

        // Log the user into the app properly
        setAuth({
          email,
          deviceID,
          auth_token: loginResponse.auth_token,
          _id: loginResponse.user._id
        })

        const tempAuth = {
          email,
          deviceID,
          auth_token: loginResponse.auth_token,
          _id: loginResponse.user._id
        }
        setTempAuthForPurchase(tempAuth)

        // Load billing details for existing user
        try {
          const invoiceData = await AldooStoreAPI.loadBillingDetails(tempAuth)
          setPreloadedInvoiceData(invoiceData)
        } catch (error) {
          setPreloadedInvoiceData(null)
        }

        // Close email dialog and continue
        setEmailDialogVisible(false)
        setPurchaseRequiresVerification(false)

        if (!checkoutVisible) {
          proceedToCheckout()
        } else {
          setCheckoutVisible(true)
        }
      } else if (loginResponse.error === "device_not_logged") {
        // Device not logged in - magic link sent, need to wait for verification
        // Return early - waitForVerification will be called separately
        return { requiresVerification: true, credentials }
      } else {
        // Other error - handle as needed
        setPurchaseRequiresVerification(false)
        setEmailDialogVisible(false)
      }
    } else {
      // Email is new - create user, log out device, then require magic link verification
      const internal_id = unwrapInternalId(await RequestInternalID({ deviceID }))
      const created = await CreateUser({ deviceID, internal_id, email })

      // Stores that let new buyers through without a magic link (config.verifyNewUsers
      // = false) use the session `create` opened: the purchase gets an owner right away
      if (!config.verifyNewUsers && created?.auth_token && created?._id) {
        const newAuth = { email, deviceID, internal_id, _id: created._id, auth_token: created.auth_token }
        setAuth(newAuth)
        setTempAuthForPurchase(newAuth)
        setPurchaseUserEmail(email)
        setPurchaseRequiresVerification(false)
        setEmailDialogVisible(false)
        PubSub.publish('Tracking_NewAccountCreated', { email })
        if (!checkoutVisible) {
          proceedToCheckout()
        } else {
          setCheckoutVisible(true)
        }
        return { requiresVerification: false, created: true }
      }

      // CreateUser authenticates the device automatically — log it out so Login() sends a magic link
      const userAPI = api("user")
      await userAPI({ safe_logout: { email, deviceID } })

      // Trigger Login to send magic link
      const loginResponse = await Login(credentials, { fetch_user_fields: ["_id"] })

      if (loginResponse.error === "device_not_logged") {
        return { requiresVerification: true, credentials }
      }

      // Edge case: device somehow already logged in — proceed directly
      if (loginResponse.result) {
        setAuth({
          email,
          deviceID,
          auth_token: loginResponse.auth_token,
          _id: loginResponse.user._id
        })
        setEmailDialogVisible(false)
        if (!checkoutVisible) {
          proceedToCheckout()
        } else {
          setCheckoutVisible(true)
        }
      }
    }

    return { requiresVerification: false }
  }

  /**
   * Wait for email verification via magic link
   */
  const waitForVerification = async (credentials) => {
    const awaitAuth = (await import("aldoo-ra/PlatformAuth/actions/await-auth")).default
    const { VerifyCredentials } = await import("aldoo-ra/PlatformAuth/api")

    // Wait for user to click magic link (5 minute timeout)
    const awaitResult = await awaitAuth(credentials)

    // Verification completed
    if (awaitResult.login) {
      // Get user ID
      const { _id } = await VerifyCredentials(credentials)

      // Log the user into the app properly
      setAuth({
        email: credentials.email,
        deviceID: credentials.deviceID,
        auth_token: awaitResult.auth_token,
        _id
      })

      // Create temp auth for API calls
      const tempAuth = {
        email: credentials.email,
        deviceID: credentials.deviceID,
        auth_token: awaitResult.auth_token,
        _id
      }
      setTempAuthForPurchase(tempAuth)

      // Load billing details for existing user
      try {
        const invoiceData = await AldooStoreAPI.loadBillingDetails(tempAuth)
        setPreloadedInvoiceData(invoiceData)
      } catch (error) {
        setPreloadedInvoiceData(null)
      }

      // Clear verification state
      setPurchaseRequiresVerification(false)
      setEmailDialogVisible(false)

      // Continue to checkout
      if (!checkoutVisible) {
        proceedToCheckout()
      } else {
        setCheckoutVisible(true)
      }

      return true
    }

    // Verification failed or timed out
    setPurchaseRequiresVerification(false)
    return false
  }

  /**
   * Proceed to checkout (after auth is set up)
   */
  const proceedToCheckout = async () => {
    // Check for billing details before showing checkout (only for logged in users)
    if (auth && config.loadBillingDetails) {
      try {
        const invoiceData = await AldooStoreAPI.loadBillingDetails()
        setPreloadedInvoiceData(invoiceData)
      } catch (error) {
        setPreloadedInvoiceData(null)
      }
    }

    setCheckoutVisible(true)
  }

  const beginCheckout = async () => {
    setPaymentReceipt(null)
    setCartVisible(false)
    // Extras belong to one checkout session (a delivery step adds them again)
    setCartExtras([])

    // Track begin_checkout
    if (cart.length) {
      const event = cartEvent(cart)
      const coupon = getValue('aldoo-promo-code') || undefined
      const savedPromoInfo = getValue('aldoo-promo-code-info')
      const value = calculateDiscountedTotal(event.value, savedPromoInfo)
      PubSub.publish('Tracking_BeginCheckout', { ...event, value, price: value, coupon })
    }

    // Check for billing details before showing checkout (only for logged in users)
    let loadedInvoiceData = null
    if (auth && config.loadBillingDetails) {
      try {
        loadedInvoiceData = await AldooStoreAPI.loadBillingDetails()
      } catch (error) {
        console.error('Error loading billing details:', error)
        // Silently handle error - user will see invoice step
      }
    }

    // Set the state with loaded data (or null)
    setPreloadedInvoiceData(loadedInvoiceData)

    // Wait for next tick to ensure state has updated
    await new Promise(resolve => setTimeout(resolve, 0))

    // Show checkout - the PurchaseFlowController will initialize the flow
    setCheckoutVisible(true)
  }

  /**
   * Add the product interface to the product object
   */
  const addProductInterface = (product) => {
    //remove one instance of the product from the cart
    product.remove = () => {
      //if the quantity is > 1, decrement the quantity
      if (product.quantity === 1) return
      //remove from the cart
      product.quantity -= 1
      refreshCart()
    }
    product.add = () => {
      //increment the quantity
      product.quantity += 1
      refreshCart()
    }
    //remove the product from the cart completely
    product.clear = () => {
      //remove from the cart
      const updatedCart = cart.filter(
        (item) => item.product_id !== product.product_id
      )
      setCart(updatedCart)
    }
  }

  /**
   * Add a product to the cart
   * @param {*} product  An object representing the product to be added to the cart
   * @param {*} proceedToCart  A boolean indicating whether to show the cart after adding the product
   * this object should have the following properties:
   * - id: the product id - this is the stable identifier for the product
   * - store_id: this is the product id as set in the Web store ( ex: Stripe, Shopify, etc)
   * - name: the product name
   * - price: the product price
   * - currency: the currency of the product
   * - quantity: the quantity of the product
   * - variant: the variant of the product
   */
  const addToCart = ({ product: item, proceedToCart = false, quantity = 1, options, selection }) => {
    // the buyer's selection ({ sku, choices, choiceLabels, components }) is part
    // of the line's identity: two sizes of one product are two lines
    const product = withSelection(item, selection)
    // Multi-item cart: merge by line key, honour quantity and options
    if (config.multiItem) {
      const key = config.lineKey(product)
      const existing = findLine(key)
      const line = existing
        ? { ...existing, quantity: (existing.quantity || 1) + quantity, options: { ...(existing.options || {}), ...(options || {}) } }
        : { ...product, quantity, options: { ...(product.options || {}), ...(options || {}) } }
      // the cart as it is now (currentCart), not this render's copy: several adds in one go
      // (a link that fills the cart) would otherwise each start from the same old lines
      const lines = currentCart()
      updateLines(existing ? lines.map((item) => (config.lineKey(item) === key ? line : item)) : [...lines, line])

      const added = cartEvent([{ ...line, quantity }])
      PubSub.publish('Tracking_AddToCart', { ...added, price: added.items[0]?.price, pid: product.pid, name: product.name })

      if (proceedToCart) showCart()
      return line
    }

    //TODO: we support a single product purchase in version 1
    //empty the cart
    while (cart.length) cart.pop()

    //check if the product is already in the cart and increment the quantity
    const existingProduct = cart.find(
      (item) => item.product_id === product.product_id
    )
    if (existingProduct) {
      existingProduct.quantity += 1
      //make sure the product has the interface
      addProductInterface(existingProduct)
      //mark as changed
      setCartChanged(true)
      //refresh the cart
      refreshCart()
      if (proceedToCart) showCart()
      return
    }
    //add the product interface to the product
    addProductInterface(product)
    //set the initial quantity to 1
    product.quantity = 1
    //add the product to the cart
    cart.push(product)
    setCart([...cart])
    //mark as changed
    setCartChanged(true)

    // Track add_to_cart (always 1 unit added)
    PubSub.publish('Tracking_AddToCart', cartEvent([{ ...product, quantity: 1 }]))

    //if proceedToCart is true, show the cart
    if (proceedToCart) showCart()
  }

  /**
   * Add a line and open the checkout right away (direct purchase links)
   */
  const buyNow = async ({ product, quantity = 1, options, selection }) => {
    addToCart({ product, quantity, options, selection })
    await beginCheckout()
  }

  const applyPromoCode = async () => {
    //mark as changed
    setCartChanged(true)

    //validate the promo code
    const response = await AldooStoreAPI.getPromoCodeInfo(promoCode)

    // Handle API errors
    if (response.error) {
      // For "coupon_already_used", use the more specific reason if available
      if (response.error === "coupon_already_used" && response.reason) {
        setPromoCodeError(response.reason) // "max_redemptions_reached" or "single_use_code"
      } else {
        setPromoCodeError(response.error) // "promo_code_not_found" or "coupon_expired"
      }
      return
    }

    // Handle success response (when server returns { result: true, ...promo_info })
    if (response.result === true) {
      setPromoCodeError(null)
      // The response already contains the promo code info along with result: true
      setPromoCodeInfo(response)
      return
    }

    // Legacy status check (in case old API format is still used)
    if (response.status && response.status !== "valid") {
      setPromoCodeError(response.status)
      return
    }

    //set the promo code error to null
    setPromoCodeError(null)
    //set the promo code details
    setPromoCodeInfo(response)
  }

  // Client-side discount calculation
  const calculateDiscountedTotal = (total, promoCodeInfo) => {
    // Return original total if no promo code or there's an error
    if (!promoCodeInfo || promoCodeInfo.status !== "valid") {
      return total
    }

    // Check if purchase meets minimum amount requirement
    if (promoCodeInfo.minimumAmount && total < promoCodeInfo.minimumAmount) {
      return total
    }

    let discountedTotal = total

    // Use the type and amount fields instead of direct percentOff/amountOff
    switch (promoCodeInfo.type) {
      case "percentage": {
        // Convert to cents, calculate discount, convert back to dollars
        // This ensures proper rounding for percentage discounts
        const totalInCents = Math.round(total * 100)
        const discountInCents = Math.round(totalInCents * (promoCodeInfo.amount / 100))
        const discountAmount = discountInCents / 100
        discountedTotal = total - discountAmount
        break
      }
      case "fixed": {
        // For fixed amount, directly subtract the discount
        discountedTotal = Math.max(0, total - promoCodeInfo.amount)
        break
      }
      default:
        return total
    }

    // Ensure we never return a negative amount
    return Math.max(0, discountedTotal)
  }

  // Sum of the lines (unit price × quantity), before promo code and extras
  const getCartSubtotal = () => {
    let total = 0
    cart.forEach((product) => {
      // Check if product has introductory pricing
      const priceModel = product.price_model?.find(pm => pm.primary)
      const phases = priceModel?.phases
      const introPhase = phases?.find(p => p.phase_type === 'introductory' && p.enabled !== false)
      const regularPhase = phases?.find(p => p.phase_type === 'regular')

      // For real currency products, always use price_model phases
      // For virtual currency products, use product.amount
      let price
      if (priceModel?.type === 'real') {
        // Real currency: use intro or regular phase amount, fallback to priceModel.amount if no phases
        price = introPhase ? introPhase.amount : (regularPhase?.amount || priceModel?.amount)
      } else {
        // Virtual currency: use product.amount
        price = product.amount
      }

      total += (price || 0) * (product.quantity || 1)
    })
    return total
  }

  const getCartExtrasTotal = () => (cartExtras || []).reduce((sum, extra) => sum + (Number(extra.amount) || 0), 0)

  // Numeric total: discounted lines + extras (major units of the cart currency)
  const getCartTotal = () => {
    if (!cart.length) return 0
    return calculateDiscountedTotal(getCartSubtotal(), promoCodeInfo) + getCartExtrasTotal()
  }

  const getCartTotalValue = () => {
    //if the cart is empty, return 0.00
    if (!cart.length) return "0.00"
    return priceDisplayFormat(getCartTotal(), cart[0]?.currency)
  }

  const getPromoCodeInfo = () => {
    if (!promoCodeInfo || promoCodeError) return ""

    const {
      promoCodeId,
      duration,
      durationInMonths = null,
      percentOff,
      amountOff,
      currency,
    } = promoCodeInfo

    if (!promoCodeId) return ""

    if (percentOff) return ` -${percentOff}%`
    if (amountOff) return ` -${priceDisplayFormat(amountOff, currency)}`

    return promoCodeInfo
  }

  /**
   * Here we are in a successful payment state
   */
  const onPaymentSuccess = async (params) => {
    checkoutCompletedRef.current = true
    hideCheckout()

    // Use captured IDs from parameters if provided, otherwise fall back to state
    const capturedPaymentIntentId = params?.paymentIntentId
    const capturedSubscriptionId = params?.subscriptionId
    const finalPaymentIntentId = capturedPaymentIntentId || paymentIntentId
    const finalSubscriptionId = capturedSubscriptionId || subscriptionId

    //Set the info for the payment success screen
    //create copies of the cart and paymentInfo
    setPaymentReceipt({
      cart: [...cart],
      extras: [...(cartExtras || [])],
      paymentInfo: { ...paymentInfo },
      paymentIntentId: finalPaymentIntentId,
      subscriptionId: finalSubscriptionId,
      requiresVerification: purchaseRequiresVerification,
      existingUserEmail: purchaseUserEmail,
      existingUserCredentials: purchaseUserCredentials,
      email: auth?.email || tempAuthForPurchase?.email || purchaseUserEmail || null,
      // Data collected by the flow steps (address, delivery, invoice…)
      stepData: getValue('purchase-flow-step-data') || {},
      // what the way it was paid adds (an order placed to be paid on delivery: no payment to wait for)
      ...(params?.receipt || {}),
    })

    // Refresh user profile to get updated account_type and other profile data
    await PlatformAuth.refreshProfile()
  }

  /**
   * An error means that the payment has failed
   * @param {*} error
   */
  const onPaymentError = (error) => {
    setPaymentError(error)
    logPaymentError(error)
  }

  const logPaymentError = (error) => {
    //Here we report the error to AldooAnalytics so we can track
    //what is the reason behind the payment failure
  }

  /**
   * The payment has succeeded but there are warnings
   * such as the payment method being expiring soon
   * or it wasn't attached properly
   * @param {*} warning
   */
  const onPaymentWarning = (warning) => {
    logPaymentError(warning)
  }

  /**
   * Get the current flow context for navigation
   */
  const getFlowContext = () => {
    // Read the latest value directly from Value store to avoid stale closure
    const latestInvoiceData = getValue("aldoo-preloaded-invoice-data")
    return {
      auth,
      tempAuthForPurchase,
      purchaseUserEmail,
      paymentInfo,
      cart,
      promoCode,
      invoiceData: latestInvoiceData,
    }
  }

  /**
   * Navigate to the next step in the flow
   */
  const goToNextStep = () => {
    if (!purchaseFlow) return null
    return purchaseFlow.goToNext(getFlowContext())
  }

  /**
   * Navigate to the previous step in the flow
   */
  const goToPreviousStep = () => {
    if (!purchaseFlow) return null
    return purchaseFlow.goBack(getFlowContext())
  }

  /**
   * Get the current step in the flow
   */
  const getCurrentFlowStep = () => {
    if (!purchaseFlow) return null
    return purchaseFlow.getCurrentStep()
  }

  //Get the render elements
  const Cart = getElement("shopping-cart")
  const Checkout = getElement("checkout")
  const PaymentSuccess = getElement("payment-success")
  const PaymentError = getElement("payment-error")
  const EmailInputDialog = getElement("email-input-dialog")

  //export the store interface
  globalThis[INSTANCE] = {
    config,
    cartChanged,
    // Multi-item cart interface
    setQuantity,
    setItemOptions,
    setLine,
    removeItem,
    findLine,
    buyNow,
    // Extra charges (shipping…) and numeric totals
    cartExtras,
    setCartExtras,
    getCartSubtotal,
    getCartTotal,
    onPaymentSuccess,
    onPaymentError,
    logPaymentError,
    onPaymentWarning,
    cartVisible,
    emailDialogVisible,
    setEmailDialogVisible,
    handleEmailVerification,
    waitForVerification,
    checkoutVisible,
    hideCheckout,
    showCheckout,
    showCart,
    hideCart,
    setCart,
    cart,
    promoCode,
    beginCheckout,
    getElement,
    getFragment,
    setPromoCode,
    resetPromoCode,
    refreshCart,
    emptyCart,
    addToCart,
    paymentSuccessVisible,
    setPaymentSuccessVisible,
    applyPromoCode,
    getPromoCodeInfo,
    promoCodeError,
    setPromoCodeError,
    getCartTotalValue,
    paymentInfo,
    setPaymentInfo,
    paymentError,
    setPaymentError,
    paymentReceipt,
    paymentIntentId,
    setPaymentIntentId,
    subscriptionId,
    setSubscriptionId,
    customAuth: tempAuthForPurchase, // Custom auth for existing users (not logged in)
    purchaseFlow, // Flow controller instance
    auth,
    tempAuthForPurchase,
    purchaseUserEmail,
    preloadedInvoiceData, // Pre-loaded billing details from beginCheckout
    setPreloadedInvoiceData, // Setter for resetting preloaded data
    // Flow navigation methods
    goToNextStep,
    goToPreviousStep,
    getCurrentFlowStep,
    getFlowContext,
  }

  // Render the store elements
  return (
    <>
      <Cart store={mountedStore()} />
      <Checkout store={mountedStore()} />
      <PaymentSuccess store={mountedStore()} />
      <PaymentError store={mountedStore()} />
    </>
  )
}

// Create a proxy to always access the current instance
export const store = new Proxy(
  {},
  {
    get: function (target, prop) {
      const instance = mountedStore()
      if (!instance) {
        console.warn("Store instance not yet initialized")
        return undefined
      }
      return instance[prop]
    },
  }
)
